/**
 * Returns a clean, digits-only national ID number (SSN/ITIN) only when the
 * supplied value is a genuine 9-digit number the applicant actually entered.
 *
 * Anything else — null/undefined, a non-string, an empty string, a value that
 * still contains the display-mask character ("*"), or a partial/eroded value —
 * returns undefined so the caller can OMIT the field from the patch entirely
 * (rather than sending a corrupted value the server would silently drop or
 * store as a bad SSN).
 *
 * This is the client-side guard for the SSN-corruption bug where a masked
 * value ("*****1234") or a programmatic autofill/erosion event could overwrite
 * the real SSN in the form model.
 */
export function sanitizeNationalIdNumber(value: unknown): string | undefined {
  if (typeof value !== "string") {
    return undefined;
  }

  // The display mask character never appears in a real, typed SSN.
  if (value.includes("*") || value.includes("X")) {
    return undefined;
  }

  const digitsOnly = value?.replace(/[^0-9]/g, "");

  return /^\d{9}$/.test(digitsOnly) ? digitsOnly : undefined;
}

/**
 * Backstop guard applied to a generated JSON Patch document before it is sent
 * to the server. It walks every operation and, for any op targeting a
 * `/nationalIdNumber` field, either normalizes the value to a clean 9-digit
 * SSN or drops the op entirely when the value is masked/empty/corrupt.
 *
 * The per-field `sanitizeNationalIdNumber` guard only protects the primary
 * applicant branch of the patch build; secondary observer-merge passes emit
 * ops straight from live observed objects. Running every patch array through
 * this function guarantees no code path can emit a corrupted SSN op.
 */
export function scrubNationalIdNumberPatch<T extends { op: string; path: string; value?: unknown }>(ops: T[]): T[] {
  if (!Array.isArray(ops)) {
    return ops;
  }

  return ops.reduce<T[]>((kept, op) => {
    // Match a trailing "/nationalIdNumber" path segment, case-insensitively.
    const targetsNationalIdNumber = /\/nationalidnumber$/i.test(op.path || "");

    if (!targetsNationalIdNumber) {
      kept.push(op);
      return kept;
    }

    // "remove" ops carry no value; leave them untouched.
    if (op.op === "remove") {
      kept.push(op);
      return kept;
    }

    const sanitized = sanitizeNationalIdNumber(op.value);
    if (sanitized === undefined) {
      // Masked / empty / corrupt — drop the op so nothing overwrites the SSN.
      return kept;
    }

    kept.push({ ...op, value: sanitized });
    return kept;
  }, []);
}
